import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_URL, getToken } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU, NAV_ORTU } from "@/lib/nav";
import { toast } from "sonner";
import { Plus, Trash2, X, Download } from "lucide-react";
import { FilterBar } from "@/components/FilterBar";
import { AutoGrowTextarea, DeskripsiButton } from "@/components/Deskripsi";
import {
  BULAN_SEM2, BULAN_SEMI, JENIS_NILAI_AKADEMIK, JENIS_NILAI_NON,
  MAPEL_AKADEMIK, MAPEL_EKSTRA, MAPEL_NON, TAHUN_AJARAN,
  computeAggregate, groupNilai, letterColor,
} from "./_nilaiHelpers";

const EMPTY = {
  siswa_id: "", jenis: "akademik", tahun_ajaran: "2025/2026", semester: "I",
  mata_pelajaran: MAPEL_AKADEMIK[0], jenis_nilai: "tugas_pr", nomor: 1,
  materi: "", aktifitas: "", bulan: "", nilai_ke: 1, skor: "", deskripsi: "",
};
const F_EMPTY = { siswa: "", jenis: "", tahun_ajaran: "", semester: "", mapel: "" };

function NilaiForm({ form, setForm, pend, existingKeys, onSubmit, onCancel }) {
  const mapelList = form.jenis === "akademik" ? MAPEL_AKADEMIK : form.jenis === "non_akademik" ? MAPEL_NON : MAPEL_EKSTRA;
  const bulanOpts = form.semester === "I" ? BULAN_SEMI : BULAN_SEM2;
  const jenisNilaiOpts = form.jenis === "akademik" ? JENIS_NILAI_AKADEMIK : JENIS_NILAI_NON;

  // Determine if current slot already exists (anti-duplicate)
  const slotKey = useMemo(() => {
    if (!form.siswa_id || !form.mata_pelajaran) return null;
    const base = `${form.siswa_id}|${form.jenis}|${form.tahun_ajaran}|${form.semester}|${form.mata_pelajaran}`;
    if (form.jenis === "ekstra") {
      if (form.jenis_nilai === "pts" || form.jenis_nilai === "pas") {
        return `${base}|${form.materi}|${form.aktifitas}|${form.jenis_nilai}`;
      }
      return `${base}|${form.materi}|${form.aktifitas}|${form.bulan}|${form.nilai_ke}`;
    }
    if (form.jenis_nilai === "tugas_pr" || form.jenis_nilai === "uh") {
      return `${base}|${form.jenis_nilai}|${form.nomor}`;
    }
    return `${base}|${form.jenis_nilai}`;
  }, [form]);
  const slotFilled = slotKey && existingKeys.has(slotKey);

  return (
    <section className="pp-card p-6 mb-6 animate-fade-up">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold">Input Nilai</h3>
        <button onClick={onCancel}><X className="w-5 h-5 text-slate-400" /></button>
      </div>
      <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4" data-testid="nilai-form">
        <div><label className="pp-label">Siswa *</label>
          <select required className="pp-input" value={form.siswa_id} onChange={(e) => setForm({ ...form, siswa_id: e.target.value })} data-testid="n-siswa">
            <option value="">Pilih siswa</option>{pend.map((p) => <option key={p.siswa_id} value={p.siswa_id}>{p.nama_siswa}</option>)}
          </select></div>
        <div><label className="pp-label">Jenis</label>
          <select className="pp-input" value={form.jenis} onChange={(e) => {
            const j = e.target.value;
            const ml = j === "akademik" ? MAPEL_AKADEMIK : j === "non_akademik" ? MAPEL_NON : MAPEL_EKSTRA;
            setForm({ ...form, jenis: j, mata_pelajaran: ml[0], jenis_nilai: j === "ekstra" ? "" : (j === "akademik" ? "tugas_pr" : "uh"), bulan: "", materi: "", aktifitas: "" });
          }}>
            <option value="akademik">Akademik</option><option value="non_akademik">Non-Akademik</option><option value="ekstra">Ekstra</option>
          </select></div>
        <div><label className="pp-label">Tahun Ajaran</label>
          <select className="pp-input" value={form.tahun_ajaran} onChange={(e) => setForm({ ...form, tahun_ajaran: e.target.value })}>
            {TAHUN_AJARAN.map((t) => <option key={t}>{t}</option>)}
          </select></div>
        <div><label className="pp-label">Semester</label>
          <select className="pp-input" value={form.semester} onChange={(e) => setForm({ ...form, semester: e.target.value, bulan: "" })}>
            <option value="I">I</option><option value="II">II</option>
          </select></div>
        <div className="sm:col-span-2"><label className="pp-label">Mata Pelajaran</label>
          <select className="pp-input" value={form.mata_pelajaran} onChange={(e) => setForm({ ...form, mata_pelajaran: e.target.value })}>
            {mapelList.map((m) => <option key={m}>{m}</option>)}
          </select></div>

        {form.jenis === "ekstra" && (
          <>
            <div><label className="pp-label">Materi *</label><input required className="pp-input" value={form.materi} onChange={(e) => setForm({ ...form, materi: e.target.value })} placeholder="contoh: mengikuti perintah" data-testid="n-materi" /></div>
            <div><label className="pp-label">Aktifitas *</label><input required className="pp-input" value={form.aktifitas} onChange={(e) => setForm({ ...form, aktifitas: e.target.value })} placeholder="contoh: lambaikan tangan" data-testid="n-aktifitas" /></div>
            <div><label className="pp-label">Jenis Nilai *</label>
              <select className="pp-input" value={form.jenis_nilai || ""} onChange={(e) => setForm({ ...form, jenis_nilai: e.target.value, bulan: "" })} data-testid="n-jn-ekstra">
                <option value="">— pilih —</option>
                <option value="bulanan">Per Bulan</option>
                <option value="pts">PTS</option>
                <option value="pas">PAS</option>
              </select>
            </div>
            {form.jenis_nilai === "bulanan" && (
              <>
                <div><label className="pp-label">Bulan *</label>
                  <select required className="pp-input" value={form.bulan} onChange={(e) => setForm({ ...form, bulan: e.target.value })}>
                    <option value="">— pilih bulan —</option>
                    {bulanOpts.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}
                  </select></div>
                <div><label className="pp-label">Nilai ke *</label>
                  <select className="pp-input" value={form.nilai_ke} onChange={(e) => setForm({ ...form, nilai_ke: parseInt(e.target.value, 10) })}>
                    {[1, 2, 3, 4].map((n) => <option key={n} value={n}>Nilai ke-{n}</option>)}
                  </select></div>
              </>
            )}
          </>
        )}

        {form.jenis !== "ekstra" && (
          <>
            <div><label className="pp-label">Jenis Nilai</label>
              <select className="pp-input" value={form.jenis_nilai} onChange={(e) => setForm({ ...form, jenis_nilai: e.target.value, nomor: 1 })} data-testid="n-jn">
                {jenisNilaiOpts.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select></div>
            {(form.jenis_nilai === "tugas_pr" || form.jenis_nilai === "uh") && (
              <div><label className="pp-label">Nomor *</label>
                <select className="pp-input" value={form.nomor} onChange={(e) => setForm({ ...form, nomor: parseInt(e.target.value, 10) })} data-testid="n-nomor">
                  {Array.from({ length: form.jenis_nilai === "tugas_pr" ? 10 : 6 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                </select></div>
            )}
          </>
        )}

        <div className="sm:col-span-2">
          {slotFilled ? (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              ⚠️ Slot ini sudah pernah diinput. Kolom nilai disembunyikan untuk menghindari duplikat.
            </div>
          ) : (
            <>
              <label className="pp-label">Nilai (0-100) *</label>
              <input type="number" min={0} max={100} step="0.01" required className="pp-input" value={form.skor} onChange={(e) => setForm({ ...form, skor: e.target.value })} data-testid="n-skor" />
            </>
          )}
        </div>
        {(form.jenis_nilai === "uts" || form.jenis_nilai === "uas") && !slotFilled && (
          <div className="sm:col-span-2">
            <label className="pp-label">Deskripsi ({form.jenis_nilai === "uts" ? "UTS" : "UAS"})</label>
            <AutoGrowTextarea
              value={form.deskripsi}
              onChange={(v) => setForm({ ...form, deskripsi: v })}
              placeholder="Tulis deskripsi / catatan untuk nilai UTS/UAS ini…"
              dataTestid="n-deskripsi"
            />
          </div>
        )}
        <div className="sm:col-span-2 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="pp-btn-secondary">Batal</button>
          <button type="submit" disabled={slotFilled} className="pp-btn-primary disabled:opacity-50" data-testid="submit-nilai">Simpan Nilai</button>
        </div>
      </form>
    </section>
  );
}

function makeSlotKey(e) {
  const base = `${e.siswa_id}|${e.jenis}|${e.tahun_ajaran}|${e.semester}|${e.mata_pelajaran}`;
  if (e.jenis === "ekstra") {
    if (e.jenis_nilai === "pts" || e.jenis_nilai === "pas") return `${base}|${e.materi}|${e.aktifitas}|${e.jenis_nilai}`;
    return `${base}|${e.materi}|${e.aktifitas}|${e.bulan}|${e.nilai_ke}`;
  }
  if (e.jenis_nilai === "tugas_pr" || e.jenis_nilai === "uh") return `${base}|${e.jenis_nilai}|${e.nomor}`;
  return `${base}|${e.jenis_nilai}`;
}

export function downloadNilaiPdf(applied, items = null) {
  const params = new URLSearchParams();
  if (applied?.jenis) params.set("jenis", applied.jenis);
  if (applied?.tahun_ajaran) params.set("tahun_ajaran", applied.tahun_ajaran);
  if (applied?.semester) params.set("semester", applied.semester);
  if (applied?.mapel) params.set("mata_pelajaran", applied.mapel);
  // Bila ada filter siswa text di kepsek, batasi ke siswa_id yang muncul
  if (applied?.siswa && Array.isArray(items)) {
    const sids = [...new Set(items.map(it => it.siswa_id))];
    sids.forEach(id => params.append("siswa_ids", id));
  }
  const url = `${API_URL}/export/nilai?${params.toString()}`;
  const t = getToken();
  fetch(url, { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: "include" })
    .then(r => r.blob())
    .then(blob => {
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = `rekap-nilai.pdf`;
      a.click(); URL.revokeObjectURL(u);
    });
}

export function NilaiGuru() {
  const [all, setAll] = useState([]);
  const [pend, setPend] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);

  const load = useCallback(async () => {
    try {
      const [{ data: n }, { data: p }] = await Promise.all([api.get("/nilai"), api.get("/pendampingan")]);
      setAll(n); setPend(p);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const existingKeys = useMemo(() => new Set(all.map(makeSlotKey)), [all]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, skor: parseFloat(form.skor) };
    // Ekstra: if "bulanan" virtual jenis_nilai, remove it before sending
    if (form.jenis === "ekstra" && form.jenis_nilai === "bulanan") {
      payload.jenis_nilai = null;
    }
    try {
      await api.post("/nilai", payload);
      toast.success("Nilai tersimpan");
      setShow(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus nilai ini?")) return;
    try { await api.delete(`/nilai/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = all.filter((e) => {
    if (applied.siswa && !(e.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    if (applied.jenis && e.jenis !== applied.jenis) return false;
    if (applied.tahun_ajaran && e.tahun_ajaran !== applied.tahun_ajaran) return false;
    if (applied.semester && e.semester !== applied.semester) return false;
    if (applied.mapel && !e.mata_pelajaran.toLowerCase().includes(applied.mapel.toLowerCase())) return false;
    return true;
  });
  const groups = groupNilai(filtered);

  return (
    <Layout nav={NAV_GURU}>
      <PageTitle title="Nilai Siswa" action={
        <div className="flex gap-2">
          <button onClick={() => downloadNilaiPdf(applied)} className="pp-btn-secondary" data-testid="dl-nilai-pdf">
            <Download className="w-4 h-4" /> Download PDF
          </button>
          <button onClick={() => { setShow(true); setForm(EMPTY); }} className="pp-btn-primary" data-testid="open-nilai">
            <Plus className="w-4 h-4" /> Input Nilai
          </button>
        </div>
      } />

      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="nilai-filter"
        fields={[
          { key: "siswa", type: "text", label: "Siswa", placeholder: "Cari siswa" },
          { key: "jenis", type: "select", label: "Jenis", options: [{ v: "akademik", label: "Akademik" }, { v: "non_akademik", label: "Non-Akademik" }, { v: "ekstra", label: "Ekstra" }] },
          { key: "tahun_ajaran", type: "select", label: "TA", options: TAHUN_AJARAN },
          { key: "semester", type: "select", label: "Sem", options: [{ v: "I", label: "I" }, { v: "II", label: "II" }] },
          { key: "mapel", type: "text", label: "Mapel", placeholder: "Cari mapel" },
        ]}
      />

      {show && <NilaiForm form={form} setForm={setForm} pend={pend} existingKeys={existingKeys} onSubmit={submit} onCancel={() => setShow(false)} />}

      <NilaiTable groups={groups} canDelete={false} />
    </Layout>
  );
}

export function NilaiOrtu() {
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  useEffect(() => { (async () => { try { const { data } = await api.get("/nilai"); setAll(data); } catch (e) { console.error(e); } })(); }, []);
  const items = all.filter((e) => {
    if (applied.jenis && e.jenis !== applied.jenis) return false;
    if (applied.tahun_ajaran && e.tahun_ajaran !== applied.tahun_ajaran) return false;
    if (applied.semester && e.semester !== applied.semester) return false;
    if (applied.mapel && !e.mata_pelajaran.toLowerCase().includes(applied.mapel.toLowerCase())) return false;
    return true;
  });
  return (
    <Layout nav={NAV_ORTU}>
      <PageTitle title="Nilai Anak" />
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="ortu-nilai-filter"
        fields={[
          { key: "jenis", type: "select", label: "Jenis", options: [{ v: "akademik", label: "Akademik" }, { v: "non_akademik", label: "Non-Akademik" }, { v: "ekstra", label: "Ekstra" }] },
          { key: "tahun_ajaran", type: "select", label: "TA", options: TAHUN_AJARAN },
          { key: "semester", type: "select", label: "Sem", options: [{ v: "I", label: "I" }, { v: "II", label: "II" }] },
          { key: "mapel", type: "text", label: "Mapel", placeholder: "Cari mapel" },
        ]}
      />
      <NilaiTable groups={groupNilai(items)} canDelete={false} />
    </Layout>
  );
}

function NilaiTable({ groups, onDelete, canDelete }) {
  if (groups.length === 0) {
    return <section className="pp-card p-10 text-center italic text-slate-400" data-testid="nilai-empty">Belum ada nilai.</section>;
  }
  return (
    <div className="space-y-6" data-testid="nilai-groups">
      {groups.map((g) => <GroupCard key={g.key} g={g} onDelete={onDelete} canDelete={canDelete} />)}
    </div>
  );
}

function GroupCard({ g, onDelete, canDelete }) {
  const agg = computeAggregate(g);
  return (
    <section className="pp-card overflow-hidden">
      <header className="px-5 py-3 border-b border-slate-200 bg-slate-50">
        <div className="font-semibold text-slate-900">{g.nama_siswa} · {g.mata_pelajaran}</div>
        <div className="text-xs text-slate-500 capitalize">{g.jenis.replace("_", " ")} · {g.tahun_ajaran} · Semester {g.semester}</div>
      </header>

      {g.jenis === "akademik" && (
        <div className="p-5 space-y-3">
          <DetailRow label="Tugas & PR" items={Array.from({length: 10}, (_, i) => i+1).map((n) => ({ no: n, val: g.entries.find((e) => e.jenis_nilai === "tugas_pr" && e.nomor === n) }))} canDelete={canDelete} onDelete={onDelete} />
          <DetailRow label="Ulangan Harian" items={Array.from({length: 6}, (_, i) => i+1).map((n) => ({ no: n, val: g.entries.find((e) => e.jenis_nilai === "uh" && e.nomor === n) }))} canDelete={canDelete} onDelete={onDelete} />
          <SingleRow label="UTS" entry={g.entries.find((e) => e.jenis_nilai === "uts")} canDelete={canDelete} onDelete={onDelete} />
          <SingleRow label="UAS" entry={g.entries.find((e) => e.jenis_nilai === "uas")} canDelete={canDelete} onDelete={onDelete} />
          <AggSummary cells={[["RUT", agg.RUT], ["RUH", agg.RUH], ["UTS", agg.UTS], ["UAS", agg.UAS], ["NR", agg.NR]]} letter={agg.letter} />
        </div>
      )}

      {g.jenis === "non_akademik" && (
        <div className="p-5 space-y-3">
          <DetailRow label="Ulangan Harian" items={Array.from({length: 6}, (_, i) => i+1).map((n) => ({ no: n, val: g.entries.find((e) => e.jenis_nilai === "uh" && e.nomor === n) }))} canDelete={canDelete} onDelete={onDelete} />
          <SingleRow label="PTS" entry={g.entries.find((e) => e.jenis_nilai === "pts")} canDelete={canDelete} onDelete={onDelete} />
          <SingleRow label="PAS" entry={g.entries.find((e) => e.jenis_nilai === "pas")} canDelete={canDelete} onDelete={onDelete} />
          <AggSummary cells={[["RUH", agg.RUH], ["PTS", agg.PTS], ["PAS", agg.PAS], ["NR", agg.NR]]} letter={agg.letter} />
        </div>
      )}

      {g.jenis === "ekstra" && agg.activities?.length > 0 && (
        <div className="p-5 space-y-5">
          {agg.activities.map((a, idx) => (
            <div key={idx} className="rounded-lg border border-slate-200 p-3">
              <div className="font-medium text-slate-800 mb-2">{a.materi} <span className="text-slate-400 text-xs italic">· {a.aktifitas}</span></div>
              <div className="grid grid-cols-4 gap-2 text-xs">
                {Object.entries(a.bulan).map(([b, vs]) => (
                  <div key={b} className="p-2 rounded bg-slate-50">
                    <div className="font-semibold text-slate-700">{b}</div>
                    <div className="text-slate-600 mt-1">N1-4: {vs.map((v, i) => v == null ? "—" : v.toFixed(0)).join(" / ")}</div>
                    <div className="text-slate-500">RT: {a.RTbulanan[b] != null ? a.RTbulanan[b].toFixed(1) : "—"}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-xs">
                <Stat label="RT" v={a.RT} />
                <Stat label="PTS" v={a.pts} />
                <Stat label="PAS" v={a.pas} />
                <Stat label="NR" v={a.NR} />
                <span className={`px-2 py-1 rounded font-bold ${letterColor(a.letter)}`}>Huruf: {a.letter}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DetailRow({ label, items, canDelete, onDelete }) {
  return (
    <div>
      <div className="text-xs uppercase text-slate-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-2">
        {items.map(({ no, val }) => (
          <div key={no} className={`px-2 py-1 rounded border text-xs flex items-center gap-1 ${val ? "bg-white border-slate-300" : "bg-slate-50 border-slate-200 text-slate-400"}`}>
            <span className="font-mono text-[10px]">#{no}</span>
            <span className={val ? "font-semibold" : ""}>{val ? val.skor.toFixed(1) : "—"}</span>
            {val && canDelete && <button onClick={() => onDelete(val.id)} className="text-rose-400 hover:text-rose-600"><Trash2 className="w-3 h-3" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SingleRow({ label, entry, canDelete, onDelete }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-xs uppercase text-slate-500 w-20">{label}</span>
      {entry ? (
        <>
          <span className="font-semibold">{entry.skor.toFixed(1)}</span>
          {entry.deskripsi && <DeskripsiButton text={entry.deskripsi} dataTestid={`deskripsi-${entry.id}`} />}
          {canDelete && <button onClick={() => onDelete(entry.id)} className="text-rose-400 hover:text-rose-600 text-xs"><Trash2 className="w-3 h-3 inline" />Hapus</button>}
        </>
      ) : <span className="text-slate-400 italic text-xs">— belum diisi —</span>}
    </div>
  );
}

function AggSummary({ cells, letter }) {
  return (
    <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap items-center gap-3 text-sm">
      {cells.map(([k, v]) => <Stat key={k} label={k} v={v} />)}
      <span className={`px-3 py-1 rounded-lg font-bold ${letterColor(letter)}`}>Huruf: {letter}</span>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="px-2 py-1 rounded bg-indigo-50 border border-indigo-100 text-xs">
      <span className="text-indigo-600 font-semibold">{label}:</span> {v != null ? Number(v).toFixed(1) : "—"}
    </div>
  );
}
